import { DomHelper } from '../modules/DomHelper';

/**
 * Accordion
 *
 * HTML 構造:
 *   <div class="c-accordion js-accordion">
 *     <button class="c-accordion__trigger js-accordion__trigger" aria-expanded="false">
 *       タイトル
 *     </button>
 *     <div class="c-accordion__content js-accordion__content" role="region">
 *       コンテンツ
 *     </div>
 *   </div>
 */
export class Accordion {
  private readonly el: HTMLElement;
  private readonly trigger: HTMLButtonElement;
  private readonly content: HTMLElement;

  constructor(el: HTMLElement) {
    this.el = el;

    const trigger = DomHelper.qs<HTMLButtonElement>('.js-accordion__trigger', el);
    const content = DomHelper.qs<HTMLElement>('.js-accordion__content', el);

    if (!trigger || !content) {
      throw new Error(
        'Accordion: .js-accordion__trigger または .js-accordion__content が見つかりません'
      );
    }

    this.trigger = trigger;
    this.content = content;
    this.init();
  }

  private init(): void {
    this.content.style.overflow = 'hidden';
    this.content.style.transition = 'height 300ms ease';

    const isOpen = this.trigger.getAttribute('aria-expanded') === 'true';
    // CSS に height: 0 が定義されていても、インラインスタイルで確実に上書きする
    this.content.style.height = isOpen ? 'auto' : '0';

    this.trigger.addEventListener('click', () => this.toggle());
  }

  toggle(): void {
    const isOpen = this.trigger.getAttribute('aria-expanded') === 'true';
    isOpen ? this.close() : this.open();
  }

  open(): void {
    this.content.style.height = `${this.content.scrollHeight}px`;
    this.trigger.setAttribute('aria-expanded', 'true');
    DomHelper.addClass(this.el, 'is-open');

    this.content.addEventListener(
      'transitionend',
      () => {
        this.content.style.height = 'auto';
      },
      { once: true }
    );
  }

  close(): void {
    // auto → px にしてからゼロへ（transition が効くように）
    this.content.style.height = `${this.content.scrollHeight}px`;
    // reflow を強制
    void this.content.offsetHeight;
    this.content.style.height = '0';
    this.trigger.setAttribute('aria-expanded', 'false');
    DomHelper.removeClass(this.el, 'is-open');
  }

  destroy(): void {
    this.trigger.removeEventListener('click', () => this.toggle());
  }

  /** ページ内の全 Accordion を初期化 */
  static initAll(selector = '.js-accordion'): Accordion[] {
    return DomHelper.qsa<HTMLElement>(selector).map((el) => new Accordion(el));
  }
}
